import { useState, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Send, X, Minus, Sparkles, Radio, Upload, Zap, ChevronDown } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_ACTIONS = [
  { label: "Go Live", icon: Zap, href: "/live", msg: "I want to go live. What do I need to do?" },
  { label: "Upload Content", icon: Upload, href: "/dashboard/upload", msg: "How do I upload content to StagePass?" },
  { label: "Start Radio", icon: Radio, href: "/dashboard/station", msg: "I want to start a radio station. Walk me through it." },
];

export default function EncoreWidget() {
  const { role, user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "What are we Premiering Today\n\nWelcome to StagePass — the creator-first platform for radio, live broadcast, and on-demand content. I'll guide you every step of the way." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Draggable state
  const [pos, setPos] = useState({ right: 20, bottom: 80 });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, right: 20, bottom: 80 });

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const sendMessage = async (text) => {
    const userMsg = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const headers = { "Content-Type": "application/json" };
      // Pass Firebase ID token for personalized Firestore context
      if (user) {
        try {
          const idToken = await user.getIdToken(false);
          headers["Authorization"] = `Bearer ${idToken}`;
        } catch (e) {
          // Token fetch failed - proceed without context
        }
      }

      const res = await fetch(`${API_URL}/api/encore/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          messages: newMessages,
          session_id: sessionId,
          user_role: role || "visitor",
          current_page: pathname,
        }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([...newMessages, { role: "assistant", content: data.response }]);
      setTimeout(scrollToBottom, 100);
    } catch (e) {
      setMessages([...newMessages, { role: "assistant", content: "I'm having a moment. Give me a second and try again." }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    sendMessage(input.trim());
  };

  const handleMouseDown = useCallback((e) => {
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, right: pos.right, bottom: pos.bottom };
    e.preventDefault();
  }, [pos]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = dragStart.current.x - e.clientX;
    const dy = dragStart.current.y - e.clientY;
    setPos({ right: Math.max(10, dragStart.current.right + dx), bottom: Math.max(70, dragStart.current.bottom + dy) });
  }, []);

  const handleMouseUp = useCallback(() => { dragging.current = false; }, []);

  if (!open) {
    return (
      <button
        data-testid="encore-open-btn"
        onClick={() => setOpen(true)}
        className="fixed z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105"
        style={{ right: pos.right, bottom: pos.bottom, background: "linear-gradient(135deg, #B026FF, #7C3AED)" }}
      >
        <Sparkles size={22} className="text-white" />
      </button>
    );
  }

  return (
    <div
      data-testid="encore-widget"
      className="fixed z-50 w-80 shadow-2xl rounded-2xl overflow-hidden border border-white/10"
      style={{ right: pos.right, bottom: pos.bottom + 60, background: "#111" }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header - draggable */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-move select-none"
        style={{ background: "linear-gradient(135deg, #1a0a2e, #0d0d1a)", borderBottom: "1px solid rgba(176,38,255,0.2)" }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#B026FF]" />
          <span className="text-white font-bold text-sm tracking-widest uppercase">Encore</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(!minimized)} className="text-zinc-500 hover:text-white p-1 rounded transition-colors">
            <Minus size={14} />
          </button>
          <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white p-1 rounded transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <div className="h-64 overflow-y-auto p-3 space-y-3 scrollbar-hide">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#B026FF] text-white"
                    : "bg-[#1a1a1a] border border-white/5 text-zinc-200"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1a1a1a] border border-white/5 rounded-xl px-3 py-2">
                  <div className="flex gap-1 items-center">
                    <div className="w-1.5 h-1.5 bg-[#B026FF] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-1.5 h-1.5 bg-[#B026FF] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-1.5 h-1.5 bg-[#B026FF] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions */}
          {messages.length <= 1 && (
            <div className="px-3 pb-2 space-y-1.5">
              {QUICK_ACTIONS.map(({ label, icon: Icon, msg }) => (
                <button
                  key={label}
                  onClick={() => sendMessage(msg)}
                  className="w-full flex items-center gap-2.5 bg-[#1a1a1a] hover:bg-[#222] border border-white/5 hover:border-[#00ff77]/20 rounded-xl px-3 py-2 transition-colors text-left"
                >
                  <Icon size={14} className="text-[#00ff77] flex-shrink-0" />
                  <span className="text-zinc-300 text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-white/5">
            <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2">
              <input
                data-testid="encore-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Encore anything..."
                className="flex-1 bg-transparent text-white text-xs focus:outline-none placeholder-zinc-600"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                data-testid="encore-send-btn"
                className="w-6 h-6 rounded-full bg-[#B026FF] flex items-center justify-center disabled:opacity-40 transition-opacity flex-shrink-0"
              >
                <Send size={10} className="text-white" />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
